import { useState, useRef, useEffect } from 'react';
import { Alert } from './ui/alert';
import { AIProvider, Conversation } from '../types/ai';
import { Assistant, ToolCallResult } from '../types/assistant';
import { ChatHeader } from './ChatHeader';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { TypingIndicator } from './TypingIndicator';
import { MessageInput } from './MessageInput';
import { ChatDropZone } from './ChatDropZone';
import { ChatMessage } from './ChatMessage';
import { ToolCallsList } from './ToolCallDisplay';
import { ToolExecutionFeedback } from './ToolExecutionFeedback';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMobile } from '../hooks/use-mobile';

interface NewChatInterfaceProps {
  provider: AIProvider;
  availableProviders: AIProvider[];
  conversation: Conversation | null;
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  onProviderChange: (providerId: string) => void;
  onSendMessage: (content: string, enabledTools?: string[], forcedTools?: string[], files?: File[]) => void;
  isLoading?: boolean;
  // Props opcionais para agentes
  assistant?: Assistant;
  onEditAssistant?: () => void;
  onShareAssistant?: () => void;
  onBackClick?: () => void;
  onNewConversation?: () => void;
  userId?: string;
  runningToolCalls?: ToolCallResult[];
  externalExecutingTools?: { id: string; name: string; status: 'pending' | 'executing' | 'completed' | 'failed' }[];
}

// Função utilitária para retornar a logo do modelo
function getModelLogo(modelId: string | undefined) {
  if (!modelId) return null;
  if (modelId.startsWith('gpt')) return <img src="/gpt-logo.png?v=1" alt="GPT Logo" className="w-5 h-5 mr-1 object-contain" />;
  if (modelId.startsWith('claude')) return <img src="/claude-logo.png?v=1" alt="Claude Logo" className="w-5 h-5 mr-1 object-contain" />;
  if (modelId.startsWith('gemini')) return <img src="/gemini-logo.png?v=1" alt="Gemini Logo" className="w-5 h-5 mr-1 object-contain" />;
  if (modelId.startsWith('perplexity')) return <img src="/perplexity-logo.png?v=1" alt="Perplexity Logo" className="w-5 h-5 mr-1 object-contain" />;
  if (modelId.startsWith('grok')) return <img src="/grok-logo.png" alt="Grok Logo" className="w-5 h-5 mr-1" />;
  if (modelId.startsWith('deepseek')) return <img src="/deepseek-logo.png" alt="DeepSeek Logo" className="w-5 h-5 mr-1" />;
  return null;
}

export function NewChatInterface({ 
  provider, 
  availableProviders,
  conversation, 
  selectedModel, 
  onModelChange, 
  onProviderChange,
  onSendMessage,
  isLoading = false,
  assistant,
  onEditAssistant,
  onShareAssistant,
  onBackClick,
  onNewConversation,
  userId,
  runningToolCalls = [],
  externalExecutingTools = []
}: NewChatInterfaceProps) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enabledTools, setEnabledTools] = useState<string[]>([]);
  const [forcedTools, setForcedTools] = useState<string[]>([]);
  const [executingTools, setExecutingTools] = useState<{ id: string; name: string; status: 'pending' | 'executing' | 'completed' | 'failed' }[]>([]);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useMobile();

  // Scroll para a última mensagem quando mensagens mudam
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  // Scroll para a última mensagem ao abrir uma conversa
  useEffect(() => {
    if (conversation) {
      // Usar setTimeout para garantir que o DOM foi renderizado
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [conversation?.id]);

  const handleSend = async (files?: File[], msgForcedTools?: string[]) => {
    if ((input.trim() || files) && !isLoading) {
      setError(null);
      const messageToSend = input.trim();
      // Limpa o input imediatamente para permitir digitar a próxima mensagem
      setInput('');
      setDroppedFiles([]);
      
      // Combinar ferramentas: disponíveis + forçadas (forçadas têm prioridade)
      const toolsToUse = [...new Set([...enabledTools, ...(msgForcedTools || [])])];
      
      // Mostrar feedback visual das ferramentas forçadas em execução
      if (msgForcedTools && msgForcedTools.length > 0) {
        const toolLabels: Record<string, string> = {
          'web_search': 'Busca na Web',
          'google_calendar': 'Google Calendar',
          'google_drive': 'Google Drive'
        };
        
        setExecutingTools(msgForcedTools.map(id => ({
          id,
          name: toolLabels[id] || id,
          status: 'executing' as const
        })));
      }
      
      try {
        // @ts-ignore - Passar arquivos como 4º parâmetro, ferramentas como 2º, e forçadas como 5º
        await onSendMessage(messageToSend, toolsToUse, msgForcedTools, files);
        
        // Marcar ferramentas como concluídas
        if (msgForcedTools && msgForcedTools.length > 0) {
          setExecutingTools(prev => prev.map(t => ({ ...t, status: 'completed' as const })));
          // Limpar após 2 segundos
          setTimeout(() => setExecutingTools([]), 2000);
        }
      } catch (err: any) {
        console.error('❌ Erro ao enviar mensagem:', err);
        setError(err?.message || 'Erro ao enviar mensagem. Tente novamente.');
        // Marcar ferramentas como falhas
        setExecutingTools(prev => prev.map(t => ({ ...t, status: 'failed' as const })));
        setTimeout(() => setExecutingTools([]), 3000);
      }
    }
  };

  const handleFilesDropped = (files: File[]) => {
    setDroppedFiles(files);
  };


  const handleModelChange = (newModelId: string) => {
    onModelChange(newModelId);
  };

  const messages = conversation?.messages || [];
  const currentModel = provider.models.find(m => m.id === selectedModel);

  return (
    <ChatDropZone onFilesDropped={handleFilesDropped} disabled={isLoading}>
      <div className="flex-1 flex flex-col h-full bg-white">
      {/* Header - ChatHeader renderiza como fixed em mobile */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200">
        <ChatHeader
          provider={provider}
          availableProviders={availableProviders}
          selectedModel={selectedModel}
          onModelChange={handleModelChange}
          onProviderChange={onProviderChange}
          assistant={assistant}
          onEditAssistant={onEditAssistant}
          onShareAssistant={onShareAssistant}
          onBackClick={onBackClick}
          onNewConversation={onNewConversation}
        />
      </div>
      
      {/* Spacer para compensar header fixed no mobile (apenas para agentes) */}
      {assistant && isMobile && (
        <div className="h-[61px] flex-shrink-0"></div>
      )}
      
      {/* Área de mensagens com rolagem */}
      <div className="flex-1 min-h-0 flex flex-col">
        {error && (
          <div className="flex-shrink-0 p-2">
            <Alert variant="destructive">{error}</Alert>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          <ScrollArea className="h-full">
          {messages.length > 0 ? (
            <div className="max-w-4xl mx-auto p-6">
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  aiLogo={provider.logo}
                  aiColor={provider.color}
                  providerId={provider.id}
                  generatedFiles={message.generatedFiles} // ✨ Passar arquivos gerados
                />
              ))}
              {/* Feedback de tools em execução */}
              {runningToolCalls.length > 0 && (
                <div className="flex justify-start px-6 py-0.5">
                  {/* Espaço do avatar para alinhar com mensagens */}
                  <div className="w-8 flex-shrink-0" />
                  <div className="ml-3">
                    <ToolCallsList toolCalls={runningToolCalls} />
                  </div>
                </div>
              )}
              {/* Feedback de ferramentas em execução */}
              {(executingTools.length > 0 || externalExecutingTools.length > 0) && (
                <ToolExecutionFeedback tools={[...executingTools, ...externalExecutingTools]} />
              )}
              {isLoading && runningToolCalls.length === 0 && executingTools.length === 0 && externalExecutingTools.length === 0 && (
                <div className="flex justify-start px-6">
                  <TypingIndicator
                    providerId={provider.id}
                    aiLogo={provider.logo}
                    aiColor={provider.color}
                  />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          ) : isLoading || executingTools.length > 0 || externalExecutingTools.length > 0 ? (
            // Mostrar feedback mesmo quando não há mensagens ainda
            <div className="max-w-4xl mx-auto p-6">
              {/* Feedback de ferramentas em execução */}
              {(executingTools.length > 0 || externalExecutingTools.length > 0) && (
                <ToolExecutionFeedback tools={[...executingTools, ...externalExecutingTools]} />
              )}
              {isLoading && executingTools.length === 0 && externalExecutingTools.length === 0 && (
                <div className="flex justify-start px-6">
                  <TypingIndicator
                    providerId={provider.id}
                    aiLogo={provider.logo}
                    aiColor={provider.color}
                  />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          ) : (
            <div className="flex-1 p-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-white border-2 rounded-full flex items-center justify-center mx-auto mb-6">
                    {provider.id === 'openai' ? (
                      <img 
                        src="/gpt-logo.png?v=1" 
                        alt="GPT Logo" 
                        className="w-10 h-10 object-contain" 
                        style={{ maxWidth: '40px', maxHeight: '40px' }}
                      />
                    ) : provider.id === 'anthropic' ? (
                      <img 
                        src="/claude-logo.png?v=1" 
                        alt="Claude Logo" 
                        className="w-10 h-10 object-contain"
                        style={{ maxWidth: '40px', maxHeight: '40px' }}
                      />
                    ) : provider.id === 'google' ? (
                      <img 
                        src="/gemini-logo.png?v=1" 
                        alt="Gemini Logo" 
                        className="w-10 h-10 object-contain"
                        style={{ maxWidth: '40px', maxHeight: '40px' }}
                      />
                    ) : provider.id === 'perplexity' ? (
                      <img 
                        src="/perplexity-logo.png?v=1" 
                        alt="Perplexity Logo" 
                        className="w-10 h-10 object-contain"
                        style={{ maxWidth: '40px', maxHeight: '40px' }}
                      />
                    ) : (
                      <span className="text-gray-600 text-xl font-semibold">{provider.icon}</span>
                    )}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {currentModel?.name || provider.name}
                  </h2>
                  <p className="text-gray-600 text-sm mb-6">
                    {currentModel?.description || provider.description}
                  </p>
                </div>



                {/* Informação sobre mudança de IA e modelo */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-blue-600">💡</span>
                    <span className="text-sm font-medium text-blue-900">Dica</span>
                  </div>
                  <p className="text-xs text-blue-700">
                    Você pode trocar a IA e o modelo a qualquer momento usando os seletores no cabeçalho. 
                    As próximas respostas usarão a nova IA e modelo selecionados.
                  </p>
                </div>
                
                <p className="text-gray-500 text-xs text-center">
                  Digite sua mensagem abaixo para começar uma conversa
                </p>
              </div>
            </div>
          )}
          </ScrollArea>
        </div>
      </div>
      
      {/* Spacer para compensar input fixed no mobile */}
      {isMobile && (
        <div className="h-[90px] flex-shrink-0"></div>
      )}
      
      {/* Input fixo no rodapé */}
      <div className={`flex-shrink-0 bg-white px-4 py-2 ${isMobile ? 'fixed bottom-0 left-0 right-0 z-40' : ''}`}>
        <div className="max-w-4xl mx-auto">
          <MessageInput
            value={input}
            onChange={setInput}
            onSend={handleSend}
            placeholder="Insira seu comando para a SPACE"
            disabled={!selectedModel || isLoading}
            externalFiles={droppedFiles}
            userId={userId}
            enabledTools={enabledTools}
            onToolsChange={setEnabledTools}
            onForcedToolsChange={setForcedTools}
          />
        </div>
      </div>
    </div>
    </ChatDropZone>
  );
}